import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { servicesData } from '../data/servicesData';

export default function PricingPage() {
  const [activeTab, setActiveTab] = useState(servicesData[0].id);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const activeService = servicesData.find(s => s.id === activeTab);

  return (
    <div className="pt-32 pb-24 px-4 min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="inline-block px-6 py-2 bg-gradient-to-r from-cyan-500/10 to-orange-500/10 border border-cyan-500/20 rounded-full text-sm font-semibold text-cyan-400 mb-6 backdrop-blur-sm">
            Transparent Pricing
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-black mb-6">
            Invest in your <span className="bg-gradient-to-r from-cyan-400 to-orange-500 bg-clip-text text-transparent">Growth</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Choose the perfect plan for your business goals. Clear, straightforward pricing with no hidden fees.
          </motion.p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {servicesData.map((service) => (
            <button
              key={service.id}
              onClick={() => setActiveTab(service.id)}
              className={`px-6 py-3 rounded-full text-sm font-bold transition-all duration-300 ${
                activeTab === service.id
                  ? `bg-gradient-to-r ${service.gradient} text-white shadow-lg`
                  : 'bg-gray-50/60 dark:bg-gray-900/60 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-white border border-gray-200 dark:border-gray-800 hover:border-gray-600'
              }`}
            >
              {service.title}
            </button>
          ))}
        </div>

        {/* Pricing Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid lg:grid-cols-3 gap-8"
          >
            {activeService.pricing.map((plan, i) => (
              <div key={i} className={`relative bg-gray-50/60 dark:bg-gray-900/60 border ${plan.isPopular ? 'border-[1.5px]' : 'border-gray-200 dark:border-gray-800'} rounded-3xl p-8 backdrop-blur-md flex flex-col transition-all hover:-translate-y-2 hover:shadow-2xl`} style={{ borderColor: plan.isPopular ? 'transparent' : '' }}>
                {plan.isPopular && (
                  <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${activeService.gradient} opacity-10 pointer-events-none`} />
                )}
                {plan.isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className={`bg-gradient-to-r ${activeService.gradient} text-white text-sm font-black px-6 py-1.5 rounded-full shadow-[0_10px_30px_-10px_rgba(14,165,233,0.5)]`}>
                      Most Popular
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 relative z-10">{plan.tier}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-8 relative z-10 min-h-[40px] leading-relaxed">{plan.description}</p>
                
                <div className="mb-8 relative z-10">
                  <span className={`text-4xl md:text-5xl font-black bg-gradient-to-r ${activeService.gradient} bg-clip-text text-transparent`}>{plan.price}</span>
                  <span className="text-gray-500 font-medium ml-2">{plan.period}</span>
                </div>
                
                <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-8" />
                
                <ul className="space-y-4 mb-10 flex-1 relative z-10">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-4">
                      <FaCheckCircle className={`mt-1 text-xl flex-shrink-0 bg-gradient-to-r ${activeService.gradient} bg-clip-text text-transparent`} style={{ color: 'transparent', WebkitBackgroundClip: 'text', backgroundImage: `linear-gradient(to right, var(--tw-gradient-from, #22d3ee), var(--tw-gradient-to, #3b82f6))` }} />
                      <span className="text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button onClick={() => navigate('/contact')} className={`relative z-10 w-full py-4 rounded-xl font-bold transition-all text-lg ${plan.isPopular ? `bg-gradient-to-r ${activeService.gradient} text-white shadow-[0_10px_30px_-10px_rgba(14,165,233,0.5)] hover:shadow-none hover:scale-[1.02]` : 'bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 border border-gray-300 dark:border-white/10'}`}>
                  Choose {plan.tier}
                </button>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
